<template>
  <div class="contents" v-loading="loading">
    <div class="title flexB">
      <h2>레벨 테스트 문제 풀 관리</h2>
    </div>

    <Navigation />

    <div class="box one filter levelTest">
      <div class="filters">
        <div class="flex">
          <p>레벨</p>
          <el-select v-model="level" clearable placeholder="전체">
            <el-option
              v-for="item in levels"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>
        <div class="flex">
          <p>문제 타입</p>
          <el-select v-model="questionType" clearable placeholder="전체">
            <el-option
              v-for="item in questionTypes"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>
        <div class="flex">
          <p>활성 여부</p>
          <el-select v-model="active" clearable placeholder="전체">
            <el-option label="활성" :value="'true'" />
            <el-option label="비활성" :value="'false'" />
          </el-select>
        </div>
        <div class="flex searchWrap">
          <p>검색</p>
          <input
            type="text"
            v-model="searchWord"
            placeholder="제목 또는 문제 문구"
            @keyup.enter="search"
          />
        </div>
      </div>
      <div class="buttonWrap">
        <button class="basic" @click="search">검색</button>
        <button class="line" @click="reset">초기화</button>
      </div>
    </div>

    <div class="box one">
      <div class="flexB mb20" style="align-items: center">
        <p class="bold">총 {{ total }}개 문항</p>
        <div class="flex bulkWrap">
          <div class="filebox">
            <label
              for="bulkUploadWordSentence"
              class="basic w100 unselect"
              title="컬럼: level, active, before, answer, after, choice1, choice2, choice3, promptTextEn, promptTextJp, explanationKo, explanationEn, explanationJp"
              >대량등록(단어/문장)</label
            >
            <input
              id="bulkUploadWordSentence"
              type="file"
              @change="(e) => handleBulkUpload(e, 'word_sentence')"
              accept=".xlsx, .xls"
            />
          </div>
          <div class="filebox">
            <label
              for="bulkUploadShortAnswer"
              class="basic w100 unselect"
              title="컬럼: level, active, promptText, answer, choice1, choice2, choice3, promptTextEn, promptTextJp, explanationKo, explanationEn, explanationJp"
              >대량등록(단답형)</label
            >
            <input
              id="bulkUploadShortAnswer"
              type="file"
              @change="(e) => handleBulkUpload(e, 'short_answer')"
              accept=".xlsx, .xls"
            />
          </div>
          <div class="filebox">
            <label
              for="bulkUploadSpeakingListen"
              class="basic w100 unselect"
              title="컬럼: level, active, promptText, promptTextEn, promptTextJp"
              >대량등록(말하기/듣기)</label
            >
            <input
              id="bulkUploadSpeakingListen"
              type="file"
              @change="(e) => handleBulkUpload(e, 'speaking_listen')"
              accept=".xlsx, .xls"
            />
          </div>
          <button class="line2 sampleBtn" @click="downloadAllBulkSamples">
            양식 다운로드(3종)
          </button>
          <button
            class="line2 sampleBtn"
            type="button"
            @click="downloadLevelTestData"
          >
            데이터 다운로드
          </button>
          <router-link class="line2" :to="registerLink">문항 등록</router-link>
        </div>
      </div>

      <table>
        <colgroup>
          <col style="width: 6%" />
          <col style="width: 8%" />
          <col style="width: 6%" />
          <col style="width: 12%" />
          <col style="width: 35%" />
          <col style="width: 8%" />
          <col style="width: 15%" />
        </colgroup>
        <tr>
          <th>
            <el-checkbox
              v-model="checkAll"
              @change="handleCheckAllChange"
            ></el-checkbox>
            <button
              @click="deleteLevelTestProblems"
              class="delete"
              v-if="ids.length > 0"
            >
              삭제
            </button>
          </th>
          <th>번호</th>
          <th>레벨</th>
          <th>문제 타입</th>
          <th>제목 / 문구</th>
          <th>활성 여부</th>
          <th>등록일</th>
        </tr>
        <tr v-for="(data, i) in problemList" :key="i">
          <td>
            <el-checkbox
              v-model="ids"
              :label="data._id"
              class="no-label-checkbox"
              @change="handleIdsChange"
            ></el-checkbox>
          </td>
          <td>{{ total - ((currentPage - 1) * 20 + i) }}</td>
          <td>{{ data.level || "-" }}</td>
          <td>{{ getQuestionTypeLabel(data.questionType) }}</td>
          <td>
            <router-link to @click.native="moveDetail(data._id)">
              {{ getProblemTitle(data) }}
            </router-link>
          </td>
          <td>{{ data.active === false ? "비활성" : "활성" }}</td>
          <td>{{ formatDate(data.createdAt) }}</td>
        </tr>
      </table>

      <div class="pagination" v-if="problemList.length > 0">
        <el-pagination
          layout="prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page="currentPage"
          @current-change="handleCurrentChange"
        />
      </div>
      <NoData v-else />
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import moment from "moment";
import {
  createLevelTestProblemBulk,
  fetchLevelTestProblems,
  removeLevelTestProblemsAll,
} from "@/api/admin";
import NoData from "@/components/contents/Nodata";
import Navigation from "../Navigation.vue";
import {
  LEVEL_TEST_LEVELS,
  LEVEL_TEST_QUESTION_TYPES,
} from "@/config/levelTest";

export default {
  name: "levelTestProblemManage",
  components: {
    Navigation,
    NoData,
  },
  data() {
    return {
      loading: false,
      moment,
      problemList: [],
      total: 0,
      pageSize: 20,
      currentPage: 1,
      searchWord: "",
      level: "",
      questionType: "",
      active: "",
      levels: LEVEL_TEST_LEVELS,
      questionTypes: LEVEL_TEST_QUESTION_TYPES,
      isUploading: false,
      ids: [],
      checkAll: false,
    };
  },
  computed: {
    registerLink() {
      const query = {};
      if (this.level) {
        query.level = this.level;
      }
      if (this.questionType) {
        query.questionType = this.questionType;
      }
      return Object.keys(query).length
        ? { path: "/admin/level-test/problems/register", query }
        : "/admin/level-test/problems/register";
    },
  },
  created() {
    this.$store.dispatch("SET_NAVBAR", "6");
  },
  mounted() {
    this.syncFromRoute();
    this.getProblemList();
  },
  methods: {
    toCellString(value) {
      return (value ?? "").toString().trim();
    },
    deleteLevelTestProblems() {
      let result = confirm("삭제 하시겠습니까?");
      if (!result) {
        return;
      }
      let data = {
        ids: this.ids,
      };
      removeLevelTestProblemsAll(data).then((res) => {
        if (res.data.status == 200) {
          alert("삭제 되었습니다.");
          this.reset();
          this.checkAll = false;
          this.ids = [];
        }
      });
    },
    handleCheckAllChange(val) {
      this.ids = val ? this.problemList.map((item) => item._id) : [];
    },
    handleIdsChange() {
      this.checkAll = this.ids.length == this.problemList.length;
    },
    parseActiveCell(value) {
      const raw = this.toCellString(value).toLowerCase();
      if (!raw) return true;
      if (raw === "false" || raw === "0" || raw === "n" || raw === "no") {
        return false;
      }
      if (raw === "true" || raw === "1" || raw === "y" || raw === "yes") {
        return true;
      }
      // 그 외 값은 "활성" 취급
      return true;
    },
    buildBulkRowByType(row = [], qt) {
      if (qt === "word_sentence") {
        const level = this.toCellString(row[0]);
        const active = this.parseActiveCell(row[1]);
        const wordSentenceBefore = this.toCellString(row[2]);
        const answer = this.toCellString(row[3]);
        const wordSentenceAfter = this.toCellString(row[4]);
        const choices = [row[5], row[6], row[7]].map(this.toCellString);
        const promptTextEn = this.toCellString(row[8]);
        const promptTextJp = this.toCellString(row[9]);
        const explanationEn = this.toCellString(row[10]);
        const explanationJp = this.toCellString(row[11]);
        const explanationKo = this.toCellString(row[12]);

        return {
          level,
          active,
          promptText: `${wordSentenceBefore}[]${wordSentenceAfter}`,
          wordSentenceBefore,
          wordSentenceAfter,
          choices,
          answers: [answer],
          promptTextEn,
          promptTextJp,
          explanationKo,
          explanationEn,
          explanationJp,
        };
      }
      if (qt === "short_answer") {
        const level = this.toCellString(row[0]);
        const active = this.parseActiveCell(row[1]);
        const promptText = this.toCellString(row[2]);
        const answer = this.toCellString(row[3]);
        const choices = [row[4], row[5], row[6]].map(this.toCellString);
        const promptTextEn = this.toCellString(row[7]);
        const promptTextJp = this.toCellString(row[8]);
        const explanationEn = this.toCellString(row[9]);
        const explanationJp = this.toCellString(row[10]);
        const explanationKo = this.toCellString(row[11]);

        return {
          level,
          active,
          promptText,
          choices,
          answers: [answer],
          promptTextEn,
          promptTextJp,
          explanationKo,
          explanationEn,
          explanationJp,
        };
      }
      // speaking_listen
      const level = this.toCellString(row[0]);
      const active = this.parseActiveCell(row[1]);
      const promptText = this.toCellString(row[2]);
      const promptTextEn = this.toCellString(row[3]);
      const promptTextJp = this.toCellString(row[4]);

      return {
        level,
        active,
        promptText,
        promptTextEn,
        promptTextJp,
      };
    },
    validateBulkRow(row, qt) {
      if (!row.level) return "레벨이 필요합니다.";
      const level = Number(row.level);
      if (!Number.isInteger(level) || level < 2 || level > 10) {
        return "레벨은 2부터 10까지만 가능합니다.";
      }
      if (qt === "word_sentence") {
        if (!row.wordSentenceBefore || !row.wordSentenceAfter) {
          return "단어/문장 유형은 빈칸 앞·뒤 문장이 필요합니다.";
        }
        if (!Array.isArray(row.choices) || row.choices.some((x) => !x)) {
          return "단어/문장 유형은 보기 3개가 필요합니다.";
        }
        if (!row.answers?.[0]) {
          return "단어/문장 유형은 정답 1개가 필요합니다.";
        }
        if (!row.choices.includes(row.answers[0])) {
          return "단어/문장 유형은 정답이 보기 3개 중 하나여야 합니다.";
        }
        return "";
      }
      if (qt === "short_answer") {
        if (!row.promptText) return "단답형 질문(promptText)이 필요합니다.";
        if (!Array.isArray(row.choices) || row.choices.some((x) => !x)) {
          return "단답형은 보기 3개가 필요합니다.";
        }
        if (!row.answers?.[0]) return "단답형은 정답 1개가 필요합니다.";
        return "";
      }
      // speaking_listen
      if (!row.promptText) return "말하기/듣기 질문(promptText)이 필요합니다.";
      return "";
    },
    getBulkTemplateConfig(qt) {
      if (qt === "word_sentence") {
        return {
          filename: "level-test-word-sentence-bulk-sample.xlsx",
          header: [
            "레벨",
            "활성 여부",
            "빈칸 앞 문장",
            "정답",
            "빈칸 뒤 문장",
            "보기1",
            "보기2",
            "보기3",
            "번역 · 영어 (선택)",
            "번역 · 일본어 (선택)",
            "해설 · 영어 (선택)",
            "해설 · 일본어 (선택)",
            "해설 · 한국어 (선택)",
          ],
          example: [
            "2",
            "true",
            "저는 아침마다",
            "커피",
            "를 마셔요.",
            "커피",
            "코피",
            "바다",
            "I drink coffee every morning.",
            "私は毎朝コーヒーを飲みます。",
            "“Drink” is used for beverages. So “커피” fits naturally because it is something you can drink, while “코피” and “바다” do not match the meaning of the sentence.",
            "「飲みます」は飲み物に使う表現です。なので、「커피」は飲めるものなので自然です。一方で、「코피」と「바다」は文の意味に合いません。",
            "빈칸에 들어갈 가장 자연스러운 단어를 고르세요.",
          ],
        };
      }
      if (qt === "short_answer") {
        return {
          filename: "level-test-short-answer-bulk-sample.xlsx",
          header: [
            "레벨",
            "활성 여부",
            "질문",
            "정답",
            "보기1",
            "보기2",
            "보기3",
            "번역 · 영어 (선택)",
            "번역 · 영어 (선택)",
            "해설 · 영어 (선택)",
            "해설 · 일본어 (선택)",
            "해설 · 한국어 (선택)",
          ],
          example: [
            "2",
            "true",
            "어제 날씨가 어땠어요?",
            "어제는 조금 추웠어요.",
            "오늘은 버스를 탔어요.",
            "어제는 조금 추웠어요.",
            "저는 운동을 좋아해요.",
            "How was the weather yesterday?",
            "昨日の天気はどうでしたか？",
            "The question is asking about yesterday’s weather. So an answer that talks about the weather or temperature—like “It was a little cold yesterday.”—is correct. Answers about the bus or exercise don’t fit the question.",
            "この質問は 「昨日の天気」 について聞いています。だから 天気や気温 に答える 「昨日は少し寒かったです。」 が正解で、バスや運動の話は質問に合いません。",
            "질문은 ‘어제 날씨’를 묻고 있어요. 그래서 날씨·기온에 대해 답하는 ‘어제는 조금 추웠어요.’가 맞고, 버스·운동 이야기는 질문과 맞지 않아요.",
          ],
        };
      }
      return {
        filename: "level-test-speaking-listen-bulk-sample.xlsx",
        header: [
          "레벨",
          "활성 여부",
          "질문",
          "번역 · 영어 (선택)",
          "번역 · 일본어 (선택)",
        ],
        example: [
          "3",
          "true",
          "너가 가장 좋아하는 동물은 뭐야?",
          "What animal do you like the most?",
          "一番好きな動物は何ですか？",
        ],
      };
    },
    downloadBulkSample(qt) {
      const config = this.getBulkTemplateConfig(qt);
      const sheet = XLSX.utils.aoa_to_sheet([config.header, config.example]);
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, "sample");
      XLSX.writeFile(book, config.filename);
    },
    downloadAllBulkSamples() {
      const types = ["word_sentence", "short_answer", "speaking_listen"];
      types.forEach((qt, idx) => {
        setTimeout(() => this.downloadBulkSample(qt), 150 * idx);
      });
    },
    formatActiveForExport(active) {
      return active === false ? "false" : "true";
    },
    getWordSentenceParts(item) {
      const hasSeparate =
        item.wordSentenceBefore !== undefined ||
        item.wordSentenceAfter !== undefined;
      if (hasSeparate) {
        return {
          before: String(item.wordSentenceBefore ?? ""),
          after: String(item.wordSentenceAfter ?? ""),
        };
      }
      const parsed = this.parseWordSentencePrompt(item.promptText || "");
      if (parsed.matched) {
        return { before: parsed.before, after: parsed.after };
      }
      return { before: "", after: "" };
    },
    buildExportRowByType(item, qt) {
      const choices = Array.isArray(item.choices) ? item.choices : [];
      const answer = (Array.isArray(item.answers) && item.answers[0]) || "";
      const active = this.formatActiveForExport(item.active);

      if (qt === "word_sentence") {
        const { before, after } = this.getWordSentenceParts(item);
        return [
          item.level ?? "",
          active,
          before,
          answer,
          after,
          choices[0] ?? "",
          choices[1] ?? "",
          choices[2] ?? "",
          item.promptTextEn ?? "",
          item.promptTextJp ?? "",
          item.explanationEn ?? "",
          item.explanationJp ?? "",
          item.explanationKo ?? item.explanation ?? "",
        ];
      }
      if (qt === "short_answer") {
        return [
          item.level ?? "",
          active,
          item.promptText ?? "",
          answer,
          choices[0] ?? "",
          choices[1] ?? "",
          choices[2] ?? "",
          item.promptTextEn ?? "",
          item.promptTextJp ?? "",
          item.explanationEn ?? "",
          item.explanationJp ?? "",
          item.explanationKo ?? item.explanation ?? "",
        ];
      }
      return [
        item.level ?? "",
        active,
        item.promptText ?? "",
        item.promptTextEn ?? "",
        item.promptTextJp ?? "",
      ];
    },
    writeLevelTestWorkbook({ headers, rows, sheetName, filename }) {
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      XLSX.writeFile(workbook, filename);
    },
    async fetchAllProblemsForExport() {
      const baseParams = {
        searchWord: this.searchWord,
        level: this.level,
        questionType: this.questionType,
        active: this.active,
      };
      const countRes = await fetchLevelTestProblems({
        ...baseParams,
        page: 1,
        limit: 1,
      });

      if (countRes.data.status !== 200) {
        throw new Error(
          countRes.data.message || "문제 목록을 불러오지 못했습니다.",
        );
      }

      const total = Number(countRes.data.total || 0);
      if (total === 0) {
        return [];
      }

      const listRes = await fetchLevelTestProblems({
        ...baseParams,
        page: 1,
        limit: total,
      });

      if (listRes.data.status !== 200) {
        throw new Error(
          listRes.data.message || "문제 목록을 불러오지 못했습니다.",
        );
      }

      return listRes.data.data || [];
    },
    async downloadLevelTestData() {
      this.loading = true;
      try {
        const problems = await this.fetchAllProblemsForExport();
        if (problems.length === 0) {
          return alert("다운로드할 데이터가 없습니다.");
        }

        const types = ["word_sentence", "short_answer", "speaking_listen"];
        const typeLabels = {
          word_sentence: "word-sentence",
          short_answer: "short-answer",
          speaking_listen: "speaking-listen",
        };
        const sheetNames = {
          word_sentence: "단어문장",
          short_answer: "단답형",
          speaking_listen: "말하기듣기",
        };
        const levelLabel = this.level || "전체";
        const dateLabel = moment().format("YYYYMMDD_HHmmss");
        const downloads = [];

        types.forEach((qt) => {
          const filtered = problems.filter((item) => item.questionType === qt);
          if (filtered.length === 0) return;

          const config = this.getBulkTemplateConfig(qt);
          downloads.push({
            headers: config.header,
            rows: filtered.map((item) => this.buildExportRowByType(item, qt)),
            sheetName: sheetNames[qt],
            filename: `level-test-data_${typeLabels[qt]}_${levelLabel}_${dateLabel}.xlsx`,
          });
        });

        if (downloads.length === 0) {
          return alert("다운로드할 데이터가 없습니다.");
        }

        downloads.forEach((item, index) => {
          setTimeout(() => {
            this.writeLevelTestWorkbook(item);
          }, index * 200);
        });
      } catch (err) {
        alert(err.message || "데이터 다운로드에 실패했습니다.");
      } finally {
        this.loading = false;
      }
    },
    handleBulkUpload(event, qt) {
      const input = event?.target;
      const file = input?.files?.[0];
      if (!file) return;
      if (this.isUploading) return;

      this.isUploading = true;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const excel = new Uint8Array(e.target.result);
          const workbook = XLSX.read(excel, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          const dataRows = rows.slice(1);

          const problems = [];
          for (let i = 0; i < dataRows.length; i += 1) {
            const rawRow = dataRows[i] || [];
            // 빈 행 스킵
            if (rawRow.every((cell) => this.toCellString(cell) === "")) {
              continue;
            }

            const built = this.buildBulkRowByType(rawRow, qt);
            const err = this.validateBulkRow(built, qt);
            if (err) {
              alert(`${i + 2}행: ${err}`);
              this.isUploading = false;
              if (input) input.value = "";
              return;
            }
            problems.push(built);
          }

          if (problems.length === 0) {
            alert("등록할 데이터가 없습니다.");
            this.isUploading = false;
            if (input) input.value = "";
            return;
          }

          createLevelTestProblemBulk({ questionType: qt, problems })
            .then((res) => {
              if (res.data.status === 200) {
                alert("대량 등록이 완료 되었습니다.");
                this.reset();
              } else {
                alert(res.data.message || "대량 등록에 실패했습니다.");
              }
            })
            .catch((err2) => {
              alert(err2.message || "대량 등록에 실패했습니다.");
            })
            .finally(() => {
              this.isUploading = false;
              if (input) input.value = "";
            });
        } catch (err) {
          alert(err.message || "엑셀 파일을 읽지 못했습니다.");
          this.isUploading = false;
          if (input) input.value = "";
        }
      };
      reader.readAsArrayBuffer(file);
    },
    formatDate(value) {
      return value ? moment(value).format("YYYY.MM.DD HH:mm") : "-";
    },
    getQuestionTypeLabel(type) {
      const matched = this.questionTypes.find((item) => item.value === type);
      return matched ? matched.label : type || "-";
    },
    parseWordSentencePrompt(text) {
      const raw = String(text || "");
      const m = raw.match(/^([\s\S]*?)\[\s*\]([\s\S]*)$/);
      if (!m) {
        return { before: "", after: "", matched: false, raw: raw.trim() };
      }
      return {
        before: m[1],
        after: m[2],
        matched: true,
      };
    },
    getWordSentenceListTitle(item) {
      const hasSeparateWordParts =
        item.wordSentenceBefore !== undefined ||
        item.wordSentenceAfter !== undefined;
      const parsed = hasSeparateWordParts
        ? {
            before: String(item.wordSentenceBefore ?? ""),
            after: String(item.wordSentenceAfter ?? ""),
            matched: true,
          }
        : this.parseWordSentencePrompt(item.promptText || "");
      if (!parsed.matched) {
        return parsed.raw || item.promptText || "-";
      }
      const answer =
        Array.isArray(item.answers) && item.answers.length
          ? String(item.answers[0] ?? "").trim()
          : "";
      // 등록 화면에서 넣은 앞·뒤 문장 그대로 이어붙임 (사이에 공백 강제 삽입 없음)
      const combined = `${parsed.before}${answer}${parsed.after}`;
      return combined.trim() || item.promptText || "-";
    },
    getProblemTitle(item) {
      if (item.questionType === "word_sentence") {
        return this.getWordSentenceListTitle(item);
      }
      return item.promptText || "-";
    },
    syncFromRoute() {
      this.currentPage = Number(this.$route.query.page || 1);
      this.searchWord = this.$route.query.searchWord || "";
      this.level = this.$route.query.level || "";
      this.questionType = this.$route.query.questionType || "";
      this.active = this.$route.query.active || "";
    },
    pushQuery() {
      const query = {
        page: String(this.currentPage),
      };
      if (this.searchWord) query.searchWord = this.searchWord;
      if (this.level) query.level = this.level;
      if (this.questionType) query.questionType = this.questionType;
      if (this.active !== "") query.active = this.active;
      this.$router.push({
        name: "levelTestProblemManage",
        query,
      });
      this.getProblemList();
    },
    search() {
      this.currentPage = 1;
      this.pushQuery();
    },
    reset() {
      this.currentPage = 1;
      this.searchWord = "";
      this.level = "";
      this.questionType = "";
      this.active = "";
      this.pushQuery();
    },
    handleCurrentChange(page) {
      this.currentPage = page;
      this.pushQuery();
    },
    moveDetail(id) {
      this.$router.push({
        name: "levelTestProblemRegister",
        query: {
          id,
          page: this.currentPage,
          searchWord: this.searchWord,
          level: this.level,
          questionType: this.questionType,
          active: this.active,
        },
      });
    },
    getProblemList() {
      this.loading = true;
      const params = {
        page: this.currentPage,
        limit: this.pageSize,
        searchWord: this.searchWord,
        level: this.level,
        questionType: this.questionType,
        active: this.active,
      };
      fetchLevelTestProblems(params)
        .then((res) => {
          if (res.data.status === 200) {
            console.log("fetchLevelTestProblems", res);
            this.problemList = res.data.data || [];
            this.total = res.data.total || 0;
          } else {
            alert(res.data.message || "문제 목록을 불러오지 못했습니다.");
          }
        })
        .catch((err) => {
          alert(err.message);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped lang="scss">
.bulkWrap {
  gap: 10px;
  align-items: center;
}
.bulkWrap .filebox label {
  width: auto !important;
  padding: 0 12px;
  box-sizing: border-box;
}
.bulkWrap .filebox input[type="file"] {
  display: none;
}
.sampleBtn {
  width: auto !important;
  // min-width: 122px;
  padding: 0 12px;
  font-weight: 400;
}
.levelTestNav {
  margin: 80px 10px 30px 30px;
}

.filters {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 20px;
}

.filters .flex {
  align-items: center;
}

.filters .flex p {
  width: 110px;
}

.searchWrap input {
  width: 100%;
}
.line2 {
  margin-left: 0 !important;
}
::v-deep .no-label-checkbox span {
  vertical-align: middle;
  line-height: 40px;
}

::v-deep .no-label-checkbox .el-checkbox__label {
  display: none;
}
button.delete {
  width: unset;
  height: unset;
  background: unset;
  border: none;
  font-size: 12px;
  margin-bottom: 4px;
  color: #9aa7c1;
}
</style>
